// src/context/EnvironmentContext.jsx
import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import environmentDB from '../services/environmentDatabase.js';

// Action types
export const ACTION_TYPES = {
  SET_ENVIRONMENT: 'SET_ENVIRONMENT',
  CREATE_ENVIRONMENT: 'CREATE_ENVIRONMENT',
  DELETE_ENVIRONMENT: 'DELETE_ENVIRONMENT',
  UPDATE_ENVIRONMENT: 'UPDATE_ENVIRONMENT',
  SET_VARIABLE: 'SET_VARIABLE',
  DELETE_VARIABLE: 'DELETE_VARIABLE',
  CLEAR_VARIABLES: 'CLEAR_VARIABLES',
  IMPORT_VARIABLES: 'IMPORT_VARIABLES',
  LOAD_STATE: 'LOAD_STATE',
  FORCE_SAVE: 'FORCE_SAVE'
};

export const ENVIRONMENT_TYPES = {
  GLOBAL: 'global',
  ENVIRONMENT: 'environment',
  COLLECTION: 'collection',
  LOCAL: 'local'
};

// Enhanced Storage Utility
const StorageUtil = {
  isAvailable: () => {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  },

  save: async (key, data, retries = 3) => {
    if (!StorageUtil.isAvailable()) {
      console.warn(`Storage not available, skipping save for: ${key}`);
      return false;
    }

    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        const serialized = JSON.stringify(data);
        localStorage.setItem(key, serialized);
        
        // Verify save
        const verify = localStorage.getItem(key);
        if (verify === serialized) {
          console.log(`Saved ${key} successfully (${Math.round(serialized.length / 1024)}KB, attempt ${attempt})`);
          return true;
        } else {
          throw new Error('Save verification failed');
        }
      } catch (error) {
        console.warn(`Save attempt ${attempt} failed for ${key}:`, error.message);
        
        if (attempt === retries) {
          console.error(`Final save attempt failed for ${key}:`, error.message);
          return false;
        }
        
        await new Promise(resolve => setTimeout(resolve, 100 * attempt));
      }
    }
    
    return false;
  },

  load: (key, defaultValue = null) => {
    if (!StorageUtil.isAvailable()) {
      console.warn(`Storage not available, returning default for: ${key}`);
      return defaultValue;
    }

    try {
      const item = localStorage.getItem(key);
      
      if (item === null || item === undefined) {
        return defaultValue;
      }

      if (item === 'null' || item === 'undefined' || item === '') {
        console.warn(`Found invalid value for key: ${key}`);
        return defaultValue;
      }

      const parsed = JSON.parse(item);
      
      if (parsed === null && defaultValue !== null) {
        console.warn(`Parsed null for key: ${key}, using default`);
        return defaultValue;
      }

      // Environment data integrity check
      if (key.includes('environments') && parsed) {
        if (typeof parsed !== 'object' || Array.isArray(parsed) || Object.keys(parsed).length === 0) {
          console.warn(`Invalid environment data for: ${key}`);
          return defaultValue;
        }
      }

      console.log(`Loaded ${key} (${Math.round(item.length / 1024)}KB)`);
      return parsed;

    } catch (error) {
      console.error(`Load failed for ${key}:`, error.message);
      
      if (error instanceof SyntaxError) {
        console.warn(`Corrupted JSON data for ${key}, removing...`);
        StorageUtil.remove(key);
      }
      
      return defaultValue;
    }
  },

  remove: (key) => {
    if (!StorageUtil.isAvailable()) return false;

    try {
      localStorage.removeItem(key);
      console.log(`Removed ${key}`);
      return true;
    } catch (error) {
      console.error(`❌ Remove failed for ${key}:`, error.message);
      return false;
    }
  }
};

// Initial state
const initialState = {
  currentEnvironment: 'dev-frontend',
  environments: {
    'dev-frontend': {
      id: 'dev-frontend',
      name: 'Development Frontend',
      description: 'Frontend geliştirme ortamı',
      color: 'bg-blue-500',
      environmentType: 'frontend',
      hiddenFields: ['id', 'createdAt', 'updatedAt'],
      variables: {
        API_URL: 'http://localhost:9071/api'
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isDefault: true
    }
  },
  globalVariables: {},
  collectionVariables: {},
  localVariables: {},
  variableHistory: [],
  settings: {
    maxHistory: 100,
    autoSave: true,
    saveInterval: 5000
  },
  persistence: {
    lastSaved: 0,
    saveCount: 0,
    version: '2.0.0'
  }
};

// Helper functions
const getVariableFromState = (state, key, scope = ENVIRONMENT_TYPES.ENVIRONMENT, envId = null) => {
  const environment = envId || state.currentEnvironment;
  
  switch (scope) {
    case ENVIRONMENT_TYPES.GLOBAL:
      return state.globalVariables[key];
    case ENVIRONMENT_TYPES.ENVIRONMENT:
      return state.environments[environment]?.variables?.[key];
    case ENVIRONMENT_TYPES.COLLECTION:
      return state.collectionVariables[key];
    case ENVIRONMENT_TYPES.LOCAL:
      return state.localVariables[key];
    default:
      return undefined;
  }
};

// Enhanced reducer with database integration
const environmentReducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.LOAD_STATE: {
      return {
        ...state,
        ...action.payload,
        persistence: {
          ...state.persistence,
          ...action.payload.persistence
        }
      };
    }

    case ACTION_TYPES.SET_ENVIRONMENT: {
      return {
        ...state,
        currentEnvironment: action.payload.environmentId,
        persistence: {
          ...state.persistence,
          lastSaved: Date.now()
        }
      };
    }

    case ACTION_TYPES.CREATE_ENVIRONMENT: {
      const { id, name, description, variables = {}, color = 'bg-blue-500', options = {} } = action.payload;
      
      return {
        ...state,
        environments: {
          ...state.environments,
          [id]: {
            id,
            name,
            description,
            color,
            environmentType: options.environmentType || 'backend',
            hiddenFields: options.hiddenFields || [],
            variables,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            isDefault: false
          }
        },
        persistence: {
          ...state.persistence,
          lastSaved: Date.now(),
          saveCount: state.persistence.saveCount + 1
        }
      };
    }

    case ACTION_TYPES.DELETE_ENVIRONMENT: {
      const { environmentId } = action.payload;
      
      // Protect default environment
      if (state.environments[environmentId]?.isDefault) {
        console.warn('Cannot delete default environment');
        return state;
      }
      
      // Ensure at least one environment exists
      if (Object.keys(state.environments).length <= 1) {
        console.warn('Cannot delete last environment');
        return state;
      }
      
      const { [environmentId]: deleted, ...remainingEnvironments } = state.environments;
      
      // If deleting current environment, switch to first available
      const newCurrentEnv = state.currentEnvironment === environmentId ?
        Object.keys(remainingEnvironments)[0] : state.currentEnvironment;
      
      return {
        ...state,
        environments: remainingEnvironments,
        currentEnvironment: newCurrentEnv,
        persistence: {
          ...state.persistence,
          lastSaved: Date.now(),
          saveCount: state.persistence.saveCount + 1
        }
      };
    }

    case ACTION_TYPES.UPDATE_ENVIRONMENT: {
      const { environmentId, updates } = action.payload;
      
      if (!state.environments[environmentId]) {
        console.warn(`Environment ${environmentId} not found`);
        return state;
      }
      
      return {
        ...state,
        environments: {
          ...state.environments,
          [environmentId]: {
            ...state.environments[environmentId],
            ...updates,
            updatedAt: new Date().toISOString()
          }
        },
        persistence: {
          ...state.persistence,
          lastSaved: Date.now(),
          saveCount: state.persistence.saveCount + 1
        }
      };
    }

    case ACTION_TYPES.SET_VARIABLE: {
      const { key, value, scope = ENVIRONMENT_TYPES.ENVIRONMENT, environment } = action.payload;
      const envId = environment || state.currentEnvironment;
      
      let newState = { ...state };
      
      // Add to history
      newState.variableHistory = [
        {
          action: 'set',
          scope,
          key,
          value,
          previousValue: getVariableFromState(state, key, scope, envId),
          timestamp: new Date().toISOString(),
          environment: envId
        },
        ...state.variableHistory.slice(0, state.settings.maxHistory - 1)
      ];
      
      switch (scope) {
        case ENVIRONMENT_TYPES.GLOBAL:
          newState.globalVariables = {
            ...state.globalVariables,
            [key]: value
          };
          break;
          
        case ENVIRONMENT_TYPES.ENVIRONMENT:
          if (newState.environments[envId]) {
            newState.environments = {
              ...state.environments,
              [envId]: {
                ...state.environments[envId],
                variables: {
                  ...state.environments[envId].variables,
                  [key]: value
                },
                updatedAt: new Date().toISOString()
              }
            };
          }
          break;
          
        case ENVIRONMENT_TYPES.COLLECTION:
          newState.collectionVariables = {
            ...state.collectionVariables,
            [key]: value
          };
          break;
          
        case ENVIRONMENT_TYPES.LOCAL:
          newState.localVariables = {
            ...state.localVariables,
            [key]: value
          };
          break;
      }
      
      newState.persistence = {
        ...state.persistence,
        lastSaved: Date.now(),
        saveCount: state.persistence.saveCount + 1
      };
      
      return newState;
    }

    case ACTION_TYPES.DELETE_VARIABLE: {
      const { key, scope = ENVIRONMENT_TYPES.ENVIRONMENT, environment } = action.payload;
      const envId = environment || state.currentEnvironment;
      
      let newState = { ...state };
      
      // Add to history
      newState.variableHistory = [
        {
          action: 'delete',
          scope,
          key,
          previousValue: getVariableFromState(state, key, scope, envId),
          timestamp: new Date().toISOString(),
          environment: envId
        },
        ...state.variableHistory.slice(0, state.settings.maxHistory - 1)
      ];
      
      switch (scope) {
        case ENVIRONMENT_TYPES.GLOBAL:
          const { [key]: deleted, ...remainingGlobal } = state.globalVariables;
          newState.globalVariables = remainingGlobal;
          break;
          
        case ENVIRONMENT_TYPES.ENVIRONMENT:
          if (newState.environments[envId]) {
            const { [key]: deletedEnv, ...remainingEnv } = state.environments[envId].variables;
            newState.environments = {
              ...state.environments,
              [envId]: {
                ...state.environments[envId],
                variables: remainingEnv,
                updatedAt: new Date().toISOString()
              }
            };
          }
          break;
          
        case ENVIRONMENT_TYPES.COLLECTION:
          const { [key]: deletedCol, ...remainingCol } = state.collectionVariables;
          newState.collectionVariables = remainingCol;
          break;
          
        case ENVIRONMENT_TYPES.LOCAL:
          const { [key]: deletedLocal, ...remainingLocal } = state.localVariables;
          newState.localVariables = remainingLocal;
          break;
      }
      
      newState.persistence = {
        ...state.persistence,
        lastSaved: Date.now(),
        saveCount: state.persistence.saveCount + 1
      };
      
      return newState;
    }

    case ACTION_TYPES.CLEAR_VARIABLES: {
      const { scope, environment } = action.payload;
      const envId = environment || state.currentEnvironment;
      
      let newState = { ...state };
      
      switch (scope) {
        case ENVIRONMENT_TYPES.GLOBAL:
          newState.globalVariables = {};
          break;
          
        case ENVIRONMENT_TYPES.ENVIRONMENT:
          if (newState.environments[envId]) {
            newState.environments = {
              ...state.environments,
              [envId]: {
                ...state.environments[envId],
                variables: {},
                updatedAt: new Date().toISOString()
              }
            };
          }
          break;
          
        case ENVIRONMENT_TYPES.COLLECTION:
          newState.collectionVariables = {};
          break;
          
        case ENVIRONMENT_TYPES.LOCAL:
          newState.localVariables = {};
          break;
      }
      
      newState.persistence = {
        ...state.persistence,
        lastSaved: Date.now(),
        saveCount: state.persistence.saveCount + 1
      };
      
      return newState;
    }

    case ACTION_TYPES.IMPORT_VARIABLES: {
      const { variables, scope, environment, merge = true } = action.payload;
      const envId = environment || state.currentEnvironment;
      
      let newState = { ...state };
      
      switch (scope) {
        case ENVIRONMENT_TYPES.GLOBAL:
          newState.globalVariables = merge 
            ? { ...state.globalVariables, ...variables }
            : variables;
          break;
          
        case ENVIRONMENT_TYPES.ENVIRONMENT:
          if (newState.environments[envId]) {
            newState.environments = {
              ...state.environments,
              [envId]: {
                ...state.environments[envId],
                variables: merge
                  ? { ...state.environments[envId].variables, ...variables }
                  : variables,
                updatedAt: new Date().toISOString()
              }
            };
          }
          break;
          
        case ENVIRONMENT_TYPES.COLLECTION:
          newState.collectionVariables = merge
            ? { ...state.collectionVariables, ...variables }
            : variables;
          break;
          
        case ENVIRONMENT_TYPES.LOCAL:
          newState.localVariables = merge
            ? { ...state.localVariables, ...variables }
            : variables;
          break;
      }
      
      newState.persistence = {
        ...state.persistence,
        lastSaved: Date.now(),
        saveCount: state.persistence.saveCount + 1
      };
      
      return newState;
    }

    case ACTION_TYPES.FORCE_SAVE: {
      return {
        ...state,
        persistence: {
          ...state.persistence,
          lastSaved: Date.now(),
          saveCount: state.persistence.saveCount + 1
        }
      };
    }

    default:
      return state;
  }
};

// Create context
const EnvironmentContext = createContext();

// Environment Provider component
export const EnvironmentProvider = ({ children }) => {
  const [state, dispatch] = useReducer(environmentReducer, initialState);
  
  // Initialize database and load data
useEffect(() => {
  const initializeData = async () => {
    try {
      console.log('Initializing environment database...');
      await environmentDB.initialize();
      console.log('Database initialized successfully');
      
      // Load environments from database
      const dbEnvironments = await environmentDB.getAllEnvironments();
      console.log('Loaded environments from DB:', dbEnvironments.length);
      
      if (dbEnvironments.length > 0) {
        const environments = {};
        let defaultEnvId = null;
        
        for (const env of dbEnvironments) {
          const variables = await environmentDB.getAllVariables(env.id);
          environments[env.id] = {
            ...env,
            variables,
            environmentType: env.environment_type,
            hiddenFields: env.hidden_fields || []
          };
          
          if (env.is_default) {
            defaultEnvId = env.id;
          }
        }
        
        // Load from localStorage for additional data
        const savedState = StorageUtil.load('tegim-environments-v2');
        
        dispatch({
          type: ACTION_TYPES.LOAD_STATE,
          payload: {
            environments,
            currentEnvironment: defaultEnvId || Object.keys(environments)[0],
            globalVariables: savedState?.globalVariables || {},
            collectionVariables: savedState?.collectionVariables || {},
            localVariables: savedState?.localVariables || {},
            variableHistory: savedState?.variableHistory || [],
            settings: savedState?.settings || initialState.settings
          }
        });
        
        console.log('Environment data loaded successfully');
      } else {
        console.log('No environments found in DB, using defaults');
        // Load from localStorage only
        const savedState = StorageUtil.load('tegim-environments-v2');
        if (savedState) {
          dispatch({
            type: ACTION_TYPES.LOAD_STATE,
            payload: savedState
          });
        }
      }
    } catch (error) {
      console.error('Failed to initialize environment data:', error);
      
      // Fallback to localStorage
      const savedState = StorageUtil.load('tegim-environments-v2');
      if (savedState) {
        dispatch({
          type: ACTION_TYPES.LOAD_STATE,
          payload: savedState
        });
      }
    }
  };

  initializeData();
}, []);

  // Auto-save to localStorage
  useEffect(() => {
    if (state.settings.autoSave) {
      const saveTimeout = setTimeout(() => {
        StorageUtil.save('tegim-environments-v2', state);
      }, state.settings.saveInterval);

      return () => clearTimeout(saveTimeout);
    }
  }, [state]);

  // Environment management functions
  const setCurrentEnvironment = useCallback((environmentId) => {
    dispatch({
      type: ACTION_TYPES.SET_ENVIRONMENT,
      payload: { environmentId }
    });
  }, []);

  const createEnvironment = useCallback(async (id, name, description, variables = {}, color = 'bg-blue-500', options = {}) => {
    try {
      // Context'te oluştur
      dispatch({
        type: ACTION_TYPES.CREATE_ENVIRONMENT,
        payload: { id, name, description, variables, color, options }
      });
      
      // Veritabanına da kaydet (eğer daha önce kaydedilmediyse)
      const existingEnv = await environmentDB.getEnvironment(id);
      if (!existingEnv) {
        await environmentDB.createEnvironment({
          id,
          name,
          description,
          base_url: variables.API_URL || 'https://api.example.com',
          environment_type: options.environmentType || 'backend',
          hidden_fields: options.hiddenFields || [],
          color
        });
        
        // Variables'ları da kaydet
        for (const [key, value] of Object.entries(variables)) {
          await environmentDB.setVariable(id, key, value);
        }
      }
      
      console.log('✅ Environment created:', { id, name });
    } catch (error) {
      console.error('❌ Create environment error:', error);
    }
  }, []);

  const updateEnvironment = useCallback((environmentId, updates) => {
    dispatch({
      type: ACTION_TYPES.UPDATE_ENVIRONMENT,
      payload: { environmentId, updates }
    });
  }, []);

  const deleteEnvironment = useCallback((environmentId) => {
    dispatch({
      type: ACTION_TYPES.DELETE_ENVIRONMENT,
      payload: { environmentId }
    });
  }, []);

  // Variable management functions
  const setVariable = useCallback(async (key, value, scope = ENVIRONMENT_TYPES.ENVIRONMENT, environment = null) => {
    try {
      const envId = environment || state.currentEnvironment;
      
      // Context'e kaydet
      dispatch({
        type: ACTION_TYPES.SET_VARIABLE,
        payload: { key, value, scope, environment }
      });
      
      // Veritabanına da kaydet
      if (scope === ENVIRONMENT_TYPES.ENVIRONMENT) {
        await environmentDB.setVariable(envId, key, value);
        console.log('✅ Variable saved to DB:', { key, value, envId });
      }
    } catch (error) {
      console.error('❌ Set variable error:', error);
    }
  }, [state]);

  const getVariable = useCallback((key, scope = ENVIRONMENT_TYPES.ENVIRONMENT, environment = null) => {
    return getVariableFromState(state, key, scope, environment);
  }, [state]);

  const deleteVariable = useCallback((key, scope = ENVIRONMENT_TYPES.ENVIRONMENT, environment = null) => {
    dispatch({
      type: ACTION_TYPES.DELETE_VARIABLE,
      payload: { key, scope, environment }
    });
  }, []);

  const clearVariables = useCallback((scope = ENVIRONMENT_TYPES.ENVIRONMENT, environment = null) => {
    dispatch({
      type: ACTION_TYPES.CLEAR_VARIABLES,
      payload: { scope, environment }
    });
  }, []);

  // Get all variables with precedence (Global -> Environment -> Collection -> Local)
  const getAllVariables = useCallback((environment = null) => {
    const envId = environment || state.currentEnvironment;
    const envVars = state.environments[envId]?.variables || {};
    
    return {
      ...state.globalVariables,
      ...envVars,
      ...state.collectionVariables,
      ...state.localVariables
    };
  }, [state]);

  // String interpolation function
  const interpolateString = useCallback((str, environment = null) => {
    if (typeof str !== 'string') return str;
    
    const variables = getAllVariables(environment);
    
    return str.replace(/\{\{([^}]+)\}\}/g, (match, key) => {
      const trimmedKey = key.trim();
      return variables[trimmedKey] !== undefined ? variables[trimmedKey] : match;
    });
  }, [getAllVariables]);

  // Request body filtering based on environment type
  const filterRequestBody = useCallback(async (body, environmentId = null) => {
    const envId = environmentId || state.currentEnvironment;
    const environment = state.environments[envId];
    
    if (!environment || environment.environmentType !== 'frontend' || !environment.hiddenFields || environment.hiddenFields.length === 0) {
      return body;
    }

    try {
      // Use database service for filtering
      return await environmentDB.filterRequestBody(envId, body);
    } catch (error) {
      console.warn('Database filtering failed, using context fallback:', error);
      
      // Fallback to context-based filtering
      const filtered = { ...body };
      environment.hiddenFields.forEach(field => {
        delete filtered[field];
      });
      return filtered;
    }
  }, [state]);

  // Import/Export functions
  const importVariables = useCallback((variables, scope = ENVIRONMENT_TYPES.ENVIRONMENT, environment = null, merge = true) => {
    dispatch({
      type: ACTION_TYPES.IMPORT_VARIABLES,
      payload: { variables, scope, environment, merge }
    });
  }, []);

  const exportVariables = useCallback((scope = ENVIRONMENT_TYPES.ENVIRONMENT, environment = null) => {
    const envId = environment || state.currentEnvironment;
    
    switch (scope) {
      case ENVIRONMENT_TYPES.GLOBAL:
        return state.globalVariables;
      case ENVIRONMENT_TYPES.ENVIRONMENT:
        return state.environments[envId]?.variables || {};
      case ENVIRONMENT_TYPES.COLLECTION:
        return state.collectionVariables;
      case ENVIRONMENT_TYPES.LOCAL:
        return state.localVariables;
      default:
        return {};
    }
  }, [state]);

  // Utility functions
  const getEnvironmentsArray = useCallback(() => {
    return Object.values(state.environments || {});
  }, [state.environments]);

  const getEnvironmentById = useCallback((id) => {
    return state.environments[id];
  }, [state.environments]);

  const getVariableStats = useCallback(() => {
    const totalVars = Object.keys(getAllVariables()).length;
    const globalVars = Object.keys(state.globalVariables).length;
    const envVars = Object.keys(state.environments[state.currentEnvironment]?.variables || {}).length;
    const collectionVars = Object.keys(state.collectionVariables).length;
    const localVars = Object.keys(state.localVariables).length;

    return {
      total: totalVars,
      global: globalVars,
      environment: envVars,
      collection: collectionVars,
      local: localVars
    };
  }, [state, getAllVariables]);

  const forceSave = useCallback(() => {
    dispatch({ type: ACTION_TYPES.FORCE_SAVE });
    return StorageUtil.save('tegim-environments-v2', state);
  }, [state]);

  const clearAllData = useCallback(() => {
    StorageUtil.remove('tegim-environments-v2');
    window.location.reload();
  }, []);

  const exportDebugData = useCallback(() => {
    return {
      ...state,
      meta: {
        exportedAt: new Date().toISOString(),
        version: state.persistence.version,
        userAgent: navigator.userAgent || 'N/A'
      }
    };
  }, [state]);

  // Context value
  const value = {
    // State
    state,
    
    // Environment management
    currentEnvironment: state.currentEnvironment,
    environments: state.environments,
    getEnvironmentsArray,
    getEnvironmentById,
    setCurrentEnvironment,
    createEnvironment,
    deleteEnvironment,
    updateEnvironment,
    
    // Variable management
    getVariable,
    getAllVariables,
    setVariable,
    deleteVariable,
    clearVariables,
    interpolateString,
    filterRequestBody,
    
    // Import/Export
    importVariables,
    exportVariables,
    
    // Utilities
    getVariableStats,
    variableHistory: state.variableHistory,
    forceSave,
    clearAllData,
    exportDebugData,
    
    // Constants
    ENVIRONMENT_TYPES,
    
    // Storage status
    storageAvailable: StorageUtil.isAvailable()
  };

  return (
    <EnvironmentContext.Provider value={value}>
      {children}
    </EnvironmentContext.Provider>
  );
};

// Custom hook to use environment context
export const useEnvironment = () => {
  const context = useContext(EnvironmentContext);
  
  if (!context) {
    throw new Error('useEnvironment must be used within an EnvironmentProvider');
  }
  
  return context;
};

// Export types and constants

export default EnvironmentContext;
